import { useCallback, useSyncExternalStore } from 'react';

/** Tracks a CSS media query; false where `matchMedia` is unavailable. */
export function useMediaQuery(query: string): boolean {
  const subscribe = useCallback(
    (onChange: () => void) => {
      if (typeof window.matchMedia !== 'function') return () => undefined;
      const list = window.matchMedia(query);
      list.addEventListener('change', onChange);
      return () => list.removeEventListener('change', onChange);
    },
    [query],
  );
  const getSnapshot = useCallback(
    () => typeof window.matchMedia === 'function' && window.matchMedia(query).matches,
    [query],
  );
  return useSyncExternalStore(subscribe, getSnapshot, () => false);
}

export const Breakpoint = {
  /** Three columns side by side: timeline, side column, assist column (2026-10b §7.2: 32 + 388 + 325 + 325 + 2 × 16). */
  Wide: '(min-width: 1102px)',
  /** Single column (phones). Between the two: the two-column layout (timeline + card stack). */
  Narrow: '(max-width: 767px)',
} as const;

export const LayoutMode = { Wide: 'wide', Medium: 'medium', Narrow: 'narrow' } as const;
export type LayoutMode = (typeof LayoutMode)[keyof typeof LayoutMode];

export function useLayoutMode(): LayoutMode {
  const wide = useMediaQuery(Breakpoint.Wide);
  const narrow = useMediaQuery(Breakpoint.Narrow);
  if (wide) return LayoutMode.Wide;
  return narrow ? LayoutMode.Narrow : LayoutMode.Medium;
}
